/**
 * @chordbaker/design - Design Tokens
 *
 * Shared spacing, component sizes, and border radii for ChordBaker applications.
 * Source of truth: React Native app (ThemeContext.tsx).
 *
 * These values are platform-agnostic (numbers, not rem/px strings)
 * so they work in both React Native and web contexts.
 */
export const spacing = {
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
};
export const sizes = {
    icon: { sm: 16, md: 20, lg: 24, xl: 32, strokeWidth: 2 },
    button: { height: 44, minWidth: 88 },
    input: { height: 48 },
    header: { height: 64, maxWidth: 896 },
    slot: { minWidth: 100, guitarHeight: 66, pianoHeight: 50, chordMinHeight: 60, lyricsMinHeight: 18 },
    hitTarget: 44,
    miniButton: 24,
    compactButton: 28,
};
export const radii = {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    xxl: 16,
    full: 999,
};
// Light-theme shadows. React Native consumers should convert to
// shadowColor / shadowOffset / shadowOpacity / shadowRadius at the call
// site (or via a helper); the string form is the canonical token.
export const shadows = {
    card: '0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04)',
    header: '0 1px 0 rgba(15,23,42,.06)',
    popover: '0 4px 12px rgba(15,23,42,.08), 0 2px 4px rgba(15,23,42,.06)',
    modal: '0 20px 40px rgba(15,23,42,.15), 0 4px 10px rgba(15,23,42,.06)',
    focus: '0 0 0 3px rgba(59,130,246,.35)',
};
// Dark-theme overrides — drop tinted opacity, use pure black with
// higher alpha so shadows stay visible against dark surfaces.
export const shadowsDark = {
    card: '0 1px 2px rgba(0,0,0,.25), 0 2px 8px rgba(0,0,0,.2)',
    header: '0 1px 0 rgba(0,0,0,.4)',
    popover: '0 4px 12px rgba(0,0,0,.4), 0 2px 4px rgba(0,0,0,.3)',
    modal: '0 20px 40px rgba(0,0,0,.6), 0 4px 10px rgba(0,0,0,.3)',
    focus: '0 0 0 3px rgba(96,165,250,.35)',
};
// React Native shadows. One recipe per elevation tier so components stop
// hand-rolling shadowOffset/opacity/radius. Spread into a style object.
export const shadowsNative = {
    card: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 2 },
    popover: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.10, shadowRadius: 8, elevation: 10 },
    modal: { shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.25, shadowRadius: 8, elevation: 8 },
    floating: { shadowColor: '#000', shadowOffset: { width: 0, height: 10 }, shadowOpacity: 0.15, shadowRadius: 20, elevation: 10 },
};
// Dark surfaces swallow low-alpha shadows; raise the alpha so elevation still reads.
export const shadowsNativeDark = {
    card: { ...shadowsNative.card, shadowOpacity: 0.25 },
    popover: { ...shadowsNative.popover, shadowOpacity: 0.35 },
    modal: { ...shadowsNative.modal, shadowOpacity: 0.5 },
    floating: { ...shadowsNative.floating, shadowOpacity: 0.4 },
};
export const motion = {
    duration: { instant: 50, fast: 150, normal: 250, slow: 400 },
    easing: {
        standard: 'cubic-bezier(0.2, 0, 0, 1)',
        emphasized: 'cubic-bezier(0.3, 0, 0, 1)',
        exit: 'cubic-bezier(0.4, 0, 1, 1)',
    },
    spring: {
        gentle: { damping: 20, stiffness: 200, mass: 1 },
        snappy: { damping: 16, stiffness: 320, mass: 0.8 },
    },
    scale: {
        pressed: 0.96,
        modalEnter: 0.94,
        pop: 1.06,
    },
};
export const opacity = {
    disabled: 0.4,
    hover: 0.08,
    pressed: 0.16,
    scrim: 0.5,
    scrimDark: 0.7,
};
// Modal cards share the `modal` native shadow, expressed in the flat
// ModalShadow shape the RN modals already spread into their styles.
const modalCardShadow = {
    offsetY: shadowsNative.modal.shadowOffset.height,
    opacity: shadowsNative.modal.shadowOpacity,
    radius: shadowsNative.modal.shadowRadius,
    elevation: shadowsNative.modal.elevation,
};
export const modalTokens = {
    standardCard: {
        borderRadius: radii.xl,
        borderWidth: 1,
        wrapperPadding: spacing.lg,
        contentPadding: spacing.xl,
        maxWidth: 400,
        shadow: modalCardShadow,
    },
    smallCard: {
        borderRadius: radii.lg,
        borderWidth: 1,
        contentPadding: spacing.xl,
        widthPercent: 80,
        maxWidth: 300,
        shadow: modalCardShadow,
    },
    bottomSheet: {
        borderRadius: radii.xxl, // top corners only
        contentPaddingH: spacing.lg,
    },
    closeButton: {
        iconSize: sizes.icon.md,
        hitArea: sizes.hitTarget,
        offsetTop: spacing.lg,
        offsetRight: spacing.lg,
    },
    title: {
        size: 18, // typography.sizes.xl
        weight: '600', // semiBold
    },
    actionButton: {
        borderRadius: radii.lg,
        height: sizes.button.height,
    },
};
export const designTokens = {
    spacing,
    sizes,
    radii,
    shadows,
    shadowsNative,
    shadowsNativeDark,
    motion,
    opacity,
    modal: modalTokens,
};
